<script lang="ts">
import { defineComponent, computed, onMounted, ref } from 'vue'
import { createNamespacedHelpers } from 'vuex-composition-helpers'
import DefaultLayout from '@/layouts/DefaultLayout.vue'
import { router, useRoute } from '../../router'
import { useI18n } from '../../setup/setup-i18n'

const { useActions: useUserStoreActions } = createNamespacedHelpers('User')

export default defineComponent({
  name: 'VerifyEmailPage',

  components: {
    DefaultLayout,
  },

  setup() {

    const { t } = useI18n()

    const currentRoute = useRoute()

    const loading = ref(false)
    const errorMessage = ref(null)

    const form = ref(null)

    const formInput = ref({
      email: null,
    })

    const token = computed(() => currentRoute.value?.query.token)

    const { validateEmailVerificationToken, verifyEmail } = useUserStoreActions(['validateEmailVerificationToken', 'verifyEmail'])

    const rules = {
      email: [
        { type: 'email', message: t('VerifyExpiredErrorPage.errorInvalidEmailInput'), trigger: 'blur' },
        { required: true, message: t('VerifyExpiredErrorPage.errorFieldRequired'), trigger: 'blur' },
      ],
    }

    const redirectToExpiredPage = () => {
      router.push({
        name: 'VerifyExpiredErrorPage',
      })
    }

    const handleSubmit = () => {

      if (!token) {
        redirectToExpiredPage()
      }

      form.value.validate().then(async (valid) => {

        if (!valid) {
          return
        }

        loading.value = true

        try {

          const { homepageUrl } = await verifyEmail({ ...formInput.value, token: token.value })

          window.location.assign(homepageUrl)

        } catch (err) {

          if (err.message === 'TokenInvalid') {
            redirectToExpiredPage()
            return
          }

          if (err.message === 'EmailInValid') {
            errorMessage.value = t('VerifyEmailPage.error.invalidEmail')
            loading.value = false
            return
          }

        } finally {
          loading.value = false
        }

      })

    }

    onMounted(async () => {

      try {
        await validateEmailVerificationToken({ token: token.value })
      } catch {
        router.push({
          name: 'VerifyExpiredErrorPage',
        })
      }

    })

    return {
      t,
      handleSubmit,
      form,
      rules,
      formInput,
      errorMessage,
      loading,
    }

  },

})

</script>

<template>
  <default-layout
    :title="t('VerifyEmailPage.pageTitle')"
  >
    <sm-form
      ref="form"
      novalidate
      :model="formInput"
      :rules="rules"
      @submit.native.prevent="handleSubmit"
    >
      <sm-input
        v-model="formInput.email"
        type="email"
        autofocus
        autocomplete="email"
        :placeholder="t('VerifyEmailPage.emailPlaceholder')"
        :label="t('VerifyEmailPage.emailLabel')"
      />

      <sm-button
        native-type="submit"
        class="block-button"
        type="primary"
        :loading="loading"
      >
        {{ t('VerifyEmailPage.submitButton') }}
      </sm-button>

      <sm-help-card
        v-if="errorMessage"
        class="verify-error"
        type="alert"
      >
        <template #header>
          {{ errorMessage }}
        </template>
      </sm-help-card>
    </sm-form>
  </default-layout>
</template>

<style lang="scss" scoped>

.verify-error {
  margin-top: 15px;
}

.block-button {
  @apply w-full;
}

::v-deep .block-button .sm-button__content {
  @apply block text-center;
}

</style>
